import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { errorTracker } from '@/lib/error-tracking';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
    level?: 'page' | 'section' | 'inline';
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        errorTracker.capture(error, {
            component: 'ErrorBoundary',
            extra: { componentStack: errorInfo.componentStack },
        });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        this.props.onReset?.();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.props.fallback) return this.props.fallback;

        const level = this.props.level ?? 'page';

        if (level === 'inline') {
            return (
                <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>Failed to load this section.</span>
                    <button onClick={this.handleReset} className="underline hover:no-underline ml-1">
                        Retry
                    </button>
                </div>
            );
        }

        const isSection = level === 'section';

        return (
            <div className={`flex flex-col items-center justify-center text-center ${isSection ? 'min-h-[200px] p-6' : 'min-h-[400px] p-8'
                }`}>
                <div className="rounded-full bg-destructive/10 p-3 mb-4">
                    <AlertTriangle className={`text-destructive ${isSection ? 'h-8 w-8' : 'h-12 w-12'}`} />
                </div>
                <h2 className={`font-semibold mb-2 ${isSection ? 'text-lg' : 'text-xl'}`}>
                    Something went wrong
                </h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                    {isSection
                        ? 'This section failed to load. Try refreshing.'
                        : 'An unexpected error occurred. Please try again or contact support if the problem persists.'}
                </p>
                <div className="flex gap-3">
                    <Button onClick={this.handleReset} variant="outline">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                    </Button>
                    {!isSection && (
                        <Button onClick={this.handleGoHome} variant="ghost">
                            <Home className="h-4 w-4 mr-2" />
                            Go Home
                        </Button>
                    )}
                </div>
                {import.meta.env.DEV && this.state.error && (
                    <details className="mt-6 text-left w-full max-w-lg">
                        <summary className="text-sm text-muted-foreground cursor-pointer">Error details</summary>
                        <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto">
                            {this.state.error.message}\n{this.state.error.stack}
                        </pre>
                    </details>
                )}
            </div>
        );
    }
}